// src/pages/EmergencyResponsePlan.tsx — G-07: Emergency Response Plan
// Site-specific ERP with evacuation procedure, emergency contacts, drill records,
// and one-tap broadcast to ALL site personnel. WHS Reg 2011 (Qld) s.43.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_ERPS, EmergencyResponsePlan as ERPType, getWHSDocumentStyle } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { AlertTriangle, Phone, Plus, X, Download, CheckCircle, ChevronRight, Bell, Shield, Users, MapPin, Sparkles, Send, Clock } from 'lucide-react'

const fmtDate = (s?: string|null) => s ? new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'}) : '—'

const STATUS_META = {
  DRAFT:    { bg:'bg-gray-100',  text:'text-gray-600',  label:'Draft' },
  APPROVED: { bg:'bg-blue-100',  text:'text-blue-700',  label:'Approved' },
  CURRENT:  { bg:'bg-green-100', text:'text-green-700', label:'Current — on site' },
}

// Emergency broadcast modal
function ERPBroadcastModal({ erp, onClose }: { erp: ERPType; onClose: ()=>void }) {
  const [sent, setSent] = useState(false)
  const [type, setType] = useState('EVACUATION')
  const BROADCAST_TYPES = [
    { value:'EVACUATION',   label:'🚨 EVACUATION — Proceed to muster point NOW',           bg:'bg-red-600' },
    { value:'FIRE',         label:'🔥 FIRE ALERT — Follow evacuation procedure',            bg:'bg-orange-600' },
    { value:'MEDICAL',      label:'🚑 MEDICAL EMERGENCY — Clear site access for ambulance', bg:'bg-red-700' },
    { value:'ALL_CLEAR',    label:'✅ ALL CLEAR — Safe to return to work',                  bg:'bg-green-600' },
    { value:'HAZMAT',       label:'⚠️ HAZMAT SPILL — Evacuate upwind immediately',          bg:'bg-amber-600' },
    { value:'SITE_CLOSURE', label:'🛑 SITE CLOSURE — All personnel must leave immediately', bg:'bg-gray-700' },
  ]
  const selected = BROADCAST_TYPES.find(b=>b.value===type)

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-red-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><Bell className="w-5 h-5 text-white"/><h3 className="font-bold text-white">Emergency broadcast — {erp.jobNumber}</h3></div>
          <button onClick={onClose}><X className="w-4 h-4 text-red-200"/></button>
        </div>
        {!sent ? (
          <div className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-xs text-red-800 font-medium">This broadcast will be sent immediately to ALL registered on-site personnel including crew, subcontractors, and visitors. Acknowledgement will be tracked per worker.</div>
            <div><label className="block text-xs font-medium text-gray-700 mb-2">Alert type</label>
              {BROADCAST_TYPES.map(b=>(
                <label key={b.value} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer mb-1.5 border ${type===b.value?'border-red-400 bg-red-50':'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="alert-type" value={b.value} checked={type===b.value} onChange={()=>setType(b.value)} className="accent-red-600"/>
                  <span className="text-sm font-medium text-gray-900">{b.label}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Muster point</label>
              <div className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700">{erp.musterPoints[0]}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"/>This is a live broadcast. Only use for genuine emergencies. False alarms are reportable under the QLD Fire and Emergency Services Act 1990.</div>
            <div className="flex gap-2"><button onClick={onClose} className="btn-secondary text-sm flex-1">Cancel</button><button onClick={()=>setSent(true)} className="text-sm flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2"><Send className="w-4 h-4"/>BROADCAST NOW</button></div>
          </div>
        ) : (
          <div className="p-6 text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto"><CheckCircle className="w-8 h-8 text-red-600"/></div>
            <div><div className="font-bold text-gray-900 text-lg">Broadcast sent</div><div className="text-sm text-gray-600 mt-1">All on-site personnel notified. Monitoring acknowledgements in MobileComms → WHS Alerts tab.</div></div>
            <button onClick={onClose} className="btn-primary w-full text-sm">Close</button>
          </div>
        )}
      </div>
    </div>
  )
}

function ERPDetail({ erp, onClose, canEdit }: { erp: ERPType; onClose: ()=>void; canEdit: boolean }) {
  const [tab, setTab] = useState<'plan'|'contacts'|'drills'>('plan')
  const [showBroadcast, setShowBroadcast] = useState(false)
  const sm = STATUS_META[erp.status]

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1"><span className="text-xs font-mono text-gray-400">{erp.erpNumber}</span><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span></div>
            <div className="font-semibold text-gray-900">{erp.jobNumber} — Emergency Response Plan</div>
            <div className="text-xs text-gray-500 mt-0.5"><MapPin className="w-3 h-3 inline mr-0.5"/>{erp.siteAddress}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>setShowBroadcast(true)} className="flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium flex-shrink-0"><Bell className="w-3.5 h-3.5"/>Emergency broadcast</button>
            <button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button>
          </div>
        </div>
      </div>

      <div className="flex border-b text-xs">
        {(['plan','contacts','drills'] as const).map(t=>(
          <button key={t} onClick={()=>setTab(t)} className={`px-4 py-2.5 font-medium border-b-2 -mb-px transition-colors capitalize ${tab===t?'border-[#2E75B6] text-[#2E75B6]':'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t==='plan'?'Site plan':t==='contacts'?`Contacts (${erp.emergencyContacts.length})`:`Drill records (${erp.drillRecords.length})`}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {tab==='plan' && (<>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="text-xs font-bold text-red-900 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5"/>EVACUATION PROCEDURE</div>
            <div className="text-sm text-red-800 whitespace-pre-wrap leading-relaxed">{erp.evacuationProcedure}</div>
          </div>
          <div><div className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1"><MapPin className="w-3 h-3"/>Muster points</div>
            {erp.musterPoints.map((mp,i)=><div key={i} className={`text-sm text-gray-700 bg-${i===0?'green':'amber'}-50 border border-${i===0?'green':'amber'}-200 rounded-lg px-3 py-2 mb-1.5`}><strong>{i===0?'Primary':'Secondary'} muster:</strong> {mp}</div>)}
          </div>
          <div><div className="text-xs font-semibold text-gray-700 mb-1">Site access instructions (for emergency services)</div><div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{erp.siteAccessInstructions}</div></div>
          <div><div className="text-xs font-semibold text-gray-700 mb-1">Nearest hospital</div><div className="text-sm text-gray-700">{erp.nearestHospital} — {erp.hospitalDistance}</div></div>
          {erp.chemicalHazardsOnSite.length>0&&<div><div className="text-xs font-semibold text-gray-700 mb-1">Chemical hazards on site</div><div className="flex flex-wrap gap-1.5">{erp.chemicalHazardsOnSite.map(c=><span key={c} className="text-xs bg-orange-100 text-orange-800 border border-orange-200 rounded-full px-2 py-0.5">{c}</span>)}</div></div>}
          {erp.specialHazards&&<div><div className="text-xs font-semibold text-gray-700 mb-1">Special hazards / infrastructure</div><div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{erp.specialHazards}</div></div>}
        </>)}

        {tab==='contacts' && (
          <div className="space-y-2">
            {erp.emergencyContacts.map((c,i)=>(
              <div key={i} className={`flex items-center justify-between rounded-xl border px-4 py-3 ${i===0?'bg-red-50 border-red-200 font-bold':i<3?'bg-amber-50 border-amber-200':'bg-white border-gray-200'}`}>
                <div><div className="text-xs text-gray-500">{c.role}</div><div className={`font-medium text-sm ${i===0?'text-red-900':i<3?'text-amber-900':'text-gray-900'}`}>{c.name}</div></div>
                <a href={`tel:${c.phone}`} className={`font-mono font-bold text-sm ${i===0?'text-red-700':i<3?'text-amber-700':'text-[#2E75B6]'}`}>{c.phone}</a>
              </div>
            ))}
          </div>
        )}

        {tab==='drills' && (
          <div className="space-y-3">
            {erp.drillRecords.map((d,i)=>(
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-medium text-gray-900 text-sm">{d.type}</div>
                  <div className="text-xs text-gray-500">{fmtDate(d.date)}</div>
                </div>
                <div className="text-xs text-gray-600 space-y-0.5"><div><Users className="w-3 h-3 inline mr-1"/>{d.participants} participants · Conducted by {d.conductedByName}</div><div className="mt-1 text-gray-700">{d.outcome}</div></div>
              </div>
            ))}
            {canEdit&&<button className="w-full btn-secondary text-sm flex items-center justify-center gap-2"><Plus className="w-4 h-4"/>Record drill</button>}
          </div>
        )}
      </div>

      <div className="border-t px-5 py-3 flex gap-2 flex-shrink-0">
        <button className="btn-secondary text-xs flex items-center gap-1"><Download className="w-3.5 h-3.5"/>Print ERP</button>
        <div className="text-xs text-gray-400 self-center ml-auto">Review: {fmtDate(erp.reviewDate)} · Approved: {erp.approvedByName}</div>
      </div>
      {showBroadcast && <ERPBroadcastModal erp={erp} onClose={()=>setShowBroadcast(false)}/>}
    </div>
  )
}

export function EmergencyResponsePlan() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')
  const [erps, setErps] = useState(MOCK_ERPS)
  const [selected, setSelected] = useState<ERPType|null>(null)
  const [showNew, setShowNew] = useState(false)

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const jobsWithoutERP = activeJobs.filter(j => !erps.some(e => e.jobId === j.id))

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><AlertTriangle className="w-6 h-6 text-red-500"/>Emergency Response Plans</h1>
          <p className="text-sm text-gray-500 mt-0.5">Site-specific ERP · evacuation procedure · emergency contacts · drill records · one-tap broadcast</p>
        </div>
        {canEdit && <button onClick={()=>setShowNew(true)} className="btn-primary text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>New ERP</button>}
      </div>

      {jobsWithoutERP.length>0&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800"><AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/><span><strong>{jobsWithoutERP.length} active job{jobsWithoutERP.length!==1?'s':''}</strong> ({jobsWithoutERP.map(j=>j.jobNumber).join(', ')}) have no ERP. WHS Regulation 2011 (Qld) s.43 requires an emergency plan at all workplaces.</span></div>}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
        <div className="font-semibold mb-1">WHS Regulation 2011 (Qld) s.43 — Emergency plan requirements</div>
        Each ERP must include: emergency procedures; testing and maintenance of emergency procedures; information, training and instruction to carry out emergency procedures. Drills must be conducted at intervals suitable to the size and complexity of the workplace.
      </div>

      <div className="flex gap-5 items-start">
        <div className={`${selected?'w-1/2':'w-full'} space-y-2 transition-all`}>
          {erps.map(erp => {
            const sm = STATUS_META[erp.status]; const isSel = selected?.id===erp.id
            const daysToReview = Math.round((new Date(erp.reviewDate).getTime() - Date.now()) / 86_400_000)
            return (
              <div key={erp.id} onClick={()=>setSelected(isSel?null:erp)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel?'border-blue-400 ring-2 ring-blue-100':daysToReview<30?'border-amber-300':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{erp.erpNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      {daysToReview<30&&<span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Review due {daysToReview<0?'overdue':`${daysToReview}d`}</span>}
                    </div>
                    <div className="font-semibold text-gray-900">{erp.jobNumber} — Emergency Response Plan</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{erp.siteAddress}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
                      <span>{erp.emergencyContacts.length} contacts</span>
                      <span>{erp.musterPoints.length} muster points</span>
                      <span>{erp.drillRecords.length} drill{erp.drillRecords.length!==1?'s':''} recorded</span>
                      <span>Review: {fmtDate(erp.reviewDate)}</span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
          {erps.length===0&&<div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No emergency response plans — create one per active job site</div>}
        </div>
        {selected && <div className="w-1/2 sticky top-6" style={{maxHeight:'calc(100vh - 160px)'}}><ERPDetail erp={selected} onClose={()=>setSelected(null)} canEdit={canEdit}/></div>}
      </div>
    </div>
  )
}
